import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Field, useFormikContext } from "formik";
import UploadChangedDocuments from "./FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import { getFullName } from "utils";
import { useDispatch, useSelector } from "react-redux";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import _get from "lodash/get";
import { getUploadChangeDocumentsAsync } from "state/slices/company";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import { Constants } from "config/constants";
import { format } from "date-fns";
import ChangesContext from "context/ChangesContext";
import AppointmentContext from "context/AppointmentContext";
import select from "config/options/select";
const NotApprovedChangedDocuments = ({
  field,
  form,
  resolvedFields = [],
  uploadRequestType = "",
  documentsFor,
  ...props
}) => {
  const { values } = useFormikContext();
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const { appointmentInfo: manageAppointment } =
    useContext(ManageModuleContext);
  const { appointmentDesignation } = useContext(AppointmentContext);
  const appointmentInfo = useMemo(() => {
    const base = appointmentDesignation
      ? { selectedDesignation: appointmentDesignation }
      : { ...(manageAppointment || {}) };

    if (form?.values?.idType) {
      base.idType = form.values.idType;
    }

    return base;
  }, [appointmentDesignation, manageAppointment, form?.values?.idType]);

  const transactionsToFilter =
    form?.values?.transactions || initiatedTransactions.items;

  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.companyDashboardSlice);
  const { articlesOfAssociation } = company;
  const templateVersions = articlesOfAssociation?.template?.versions ?? [];
  const latestTemplateVersion =
    templateVersions[templateVersions.length - 1]?.summary;
  const [documentName, setDocumentName] = useState("");
  const currentUser = useSelector((state) => state.userSlice);

  const { setFieldValue } = useFormikContext();
  const handleSetValue = (e) => {
    const selectedValue = e.target.value;
    setFieldValue(field.name, {
      ...field.value,
      [e.target.name]: selectedValue,
    });
  };
  const isGeneratingRef = useRef(false);

  const getConsentDocument = useCallback(async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    try {
      let payload;

      if (uploadRequestType === "Director Approval") {
        const filteredTransactions = transactionsToFilter.filter(
          (transaction) =>
            transaction.approvalType === "boardApproval" ||
            transaction?.authorizations?.length
        );

        payload = {
          officialType: "authorized person",
          fileType: Constants.Template_FILE_TYPES.BOD_RESOLS_CHANGES,
          typeOfChangeForMaintainCompany: "appointment",
          multipleAppointments: transactionsToFilter.length > 0,
          transactions: filteredTransactions,
          boardResolutionArticle:
            latestTemplateVersion?.resolutionApprovalSignatories?.for ||
            articlesOfAssociation?.template,
        };
      } else if (uploadRequestType === "Shareholder Approval") {
        const filteredTransactions = transactionsToFilter.filter(
          (transaction) => transaction.approvalType === "shareholderApproval"
        );

        payload = {
          officialType: "authorized person",
          fileType: Constants.Template_FILE_TYPES.SHAREHOLDER_APPROVAL,
          typeOfChangeForMaintainCompany: "appointment",
          transactions: filteredTransactions,
          boardResolutionArticle:
            latestTemplateVersion?.resolutionApprovalSignatories?.for ||
            articlesOfAssociation?.template,
        };
      } else if (uploadRequestType === "appointment-letter") {
        payload = {
          ...form.values,
          appointers: values?.directorsList || [],
          appointmentInfo,
          appointmentDate:
            form.values.identificationDetails.appointmentDate ||
            form.values.appointmentDate.appointmentDate ||
            new Date(),
          fileType: Constants.Template_FILE_TYPES.APPOINTMENT_LETTER,
        };
      } else {
        payload = {
          ...form.values,
          appointmentInfo,
          appointmentDate:
            form.values.identificationDetails.appointmentDate ||
            form.values.appointmentDate.appointmentDate ||
            new Date(),
          fileType: Constants.Template_FILE_TYPES.CONSENT_TO_ACT,
        };
      }

      const data = await dispatch(
        getUploadChangeDocumentsAsync({
          companyId: company._id,
          data: payload,
        })
      );

      const fileName = data?.payload?.fileName;

      if (fileName) {
        setFieldValue(field.name, {
          ...field.value,
          consentResolutionDocumentName: fileName,
        });
        setDocumentName(fileName);
        setFieldValue("consentResolutionTypeNeeded", false);
      }
    } finally {
      isGeneratingRef.current = false;
    }
  }, [
    uploadRequestType,
    transactionsToFilter,
    latestTemplateVersion,
    articlesOfAssociation,
    form.values,
    values?.directorsList,
    appointmentInfo,
    dispatch,
    company._id,
    setFieldValue,
    field.name,
    field.value,
  ]);
  const { isInChangesModule } = useContext(ChangesContext);

  const resolutionKey =
    uploadRequestType === "Director Approval"
      ? "directorResolution"
      : uploadRequestType === "Shareholder Approval"
        ? "shareholderResolution"
        : uploadRequestType === "appointment-letter"
          ? "appointmentResolutionType"
          : "consentResolutionType";

  const resolutionType = field.value?.[resolutionKey];
  const consentResolutionDocumentName =
    field.value?.consentResolutionDocumentName;

  useEffect(() => {
    if (
      resolutionType === "platformResolutionType" &&
      !consentResolutionDocumentName
    ) {
      getConsentDocument();
      return;
    }

    setDocumentName(consentResolutionDocumentName || "");
  }, [resolutionType, consentResolutionDocumentName, getConsentDocument]);

  return (
    <div className=" bg-white px-4 py-6 mt-2 mb-4 shadow-sm rounded-md">
      <h3 className="mb-2">
        {documentsFor === "uploadChangedDocuments"
          ? "Consent To Act Forms"
          : documentsFor === "appointment-letter"
            ? "Appointment Letter"
            : uploadRequestType === "Director Approval"
              ? "Board Resolutions approving the change(s)"
              : uploadRequestType === "Shareholder Approval"
                ? "Shareholder Resolutions approving the change(s)"
                : "Gazette Notice approving the change(s)"}
      </h3>
      <div className="bg-[#EDF0F5]  flex flex-col justify-evenly mb-4 px-4 py-4 shadow-sm rounded-md space-y-3">
        <div className="flex items-center">
          <label
            htmlFor="consentResolutionType-ownResolutionType"
            className="flex items-center cursor-pointer"
          >
            <Field
              id="consentResolutionType-ownResolutionType"
              //name="consentResolutionType"
              name={
                uploadRequestType === "Director Approval"
                  ? "directorResolution"
                  : uploadRequestType === "Shareholder Approval"
                    ? "shareholderResolution"
                    : documentsFor === "appointment-letter"
                      ? "appointmentResolutionType"
                      : "consentResolutionType"
              }
              type="radio"
              form={form}
              value="ownResolutionType"
              checked={
                field.value[
                uploadRequestType === "Director Approval"
                  ? "directorResolution"
                  : uploadRequestType === "Shareholder Approval"
                    ? "shareholderResolution"
                    : documentsFor === "appointment-letter"
                      ? "appointmentResolutionType"
                      : "consentResolutionType"
                ] === "ownResolutionType"
              }
              disabled={false}
              onBlur={field.onBlur}
              onChange={handleSetValue}
              className={`cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50`}
            />
            <span className="ml-2">
              {documentsFor === "uploadChangedDocuments"
                ? " Upload your own Consent To Act Form"
                : documentsFor === "appointment-letter"
                  ? " Upload your own Appointment Letter"
                  : uploadRequestType === "Director Approval"
                    ? "Upload your own Board Resolutions"
                    : uploadRequestType === "Shareholder Approval"
                      ? "Upload your own Shareholder Resolutions"
                      : "Upload your own Gazette Resolutions"}
            </span>
          </label>
        </div>
        {field.value[
          uploadRequestType === "Director Approval"
            ? "directorResolution"
            : uploadRequestType === "Shareholder Approval"
              ? "shareholderResolution"
              : uploadRequestType === "appointment-letter"
                ? "appointmentResolutionType"
                : "consentResolutionType"
        ] === "ownResolutionType" && (
            <Field
              name="fileUploads"
              component={UploadChangedDocuments}
              documentData={getChangedDocumentsModuleData(
                uploadRequestType === "Director Approval"
                  ? "ownStatutoryTemplateForDirectorApproval"
                  : uploadRequestType === "Shareholder Approval"
                    ? "ownStatutoryTemplateForShareholderApproval"
                    : "ownConsentResolutionType"
              )}
              disabled={false}
            />
          )}
        <div className="flex items-center">
          <label
            htmlFor="consentResolutionType-platformResolutionType"
            className="flex items-center cursor-pointer"
          >
            <Field
              id="consentResolutionType-platformResolutionType"
              name={
                uploadRequestType === "Director Approval"
                  ? "directorResolution"
                  : uploadRequestType === "Shareholder Approval"
                    ? "shareholderResolution"
                    : documentsFor === "appointment-letter"
                      ? "appointmentResolutionType"
                      : "consentResolutionType"
              }
              //name="consentResolutionType"
              form={form}
              type={"radio"}
              value={"platformResolutionType"}
              checked={
                field.value[
                uploadRequestType === "Director Approval"
                  ? "directorResolution"
                  : uploadRequestType === "Shareholder Approval"
                    ? "shareholderResolution"
                    : documentsFor === "appointment-letter"
                      ? "appointmentResolutionType"
                      : "consentResolutionType"
                ] === "platformResolutionType"
              }
              // errorPath="postalAddress.postalAddress"
              disabled={false}
              onBlur={field.onBlur}
              onChange={handleSetValue}
              // onClick={getConsentDocument}
              className={`cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50`}
            />
            <span className="ml-2">
              {documentsFor === "uploadChangedDocuments"
                ? " Adopting our Consent To Act Template"
                : documentsFor === "appointment-letter"
                  ? "Adopting our Appointment Template"
                  : uploadRequestType === "Director Approval"
                    ? "Adopting our Board Resolutions Template"
                    : uploadRequestType === "Shareholder Approval"
                      ? "Adopting our Shareholder Resolutions Template"
                      : "Adopting our Gazette Resolutions Template"}
            </span>
          </label>
        </div>
        {field.value[
          uploadRequestType === "Director Approval"
            ? "directorResolution"
            : uploadRequestType === "Shareholder Approval"
              ? "shareholderResolution"
              : uploadRequestType === "appointment-letter"
                ? "appointmentResolutionType"
                : "consentResolutionType"
        ] === "platformResolutionType" && (
            <>
              <div className=" bg-white px-4 py-4 mt-2 mb-4 shadow-sm  border-b-gray-500">
                {documentName ? (
                  <OnlyOfficeDocumentEditor
                    url={`https://sfl-bucket.s3.us-west-2.amazonaws.com/images/${documentName}`}
                    editorKey={`${currentUser._id}-${company._id}-${documentName}`}
                    userFullName={getFullName(currentUser.names)}
                    userId={currentUser._id}
                    companyId={company._id}
                    documentType={"word"}
                    fileType={"docx"}
                    title={`${format(new Date(), "yyyy/MM/dd")} - ${documentsFor === "uploadChangedDocuments"
                      ? "Consent to Act"
                      : uploadRequestType === "Director Approval"
                        ? "BoD Resols - Changes"
                        : uploadRequestType === "Shareholder Approval"
                          ? "Shareholder Resols - Changes"
                          : ""
                      }`}
                    mode={"edit"}
                    zoom={-2}
                    containerHeight={"600px"}
                    showFullScreenToggle={true}
                    permissions={{
                      edit: true,
                      review: false,
                      comment: true,
                      editCommentAuthorOnly: false,
                      deleteCommentAuthorOnly: false,
                    }}
                    onDocumentReady={() => { }}
                    onLoadComponentError={(errorCode, errorDescription) => { }}
                    onDocumentStateChange={(event) => { }}
                    onError={(error) => { }}
                    handleSave={(event) => { }}
                    metadata={{
                      isFromChangesModule: "isFromChangesModule",
                      fileName: documentName,
                    }}
                  />
                ) : (
                  <p className="flex justify-center py-4">
                    <SpinnerIcon className="text-gray-500" />
                  </p>
                )}
              </div>
            </>
          )}
      </div>
    </div>
  );
};
export default NotApprovedChangedDocuments;
